/**
 * Catálogo de procedimientos de Endomedical IPS.
 *
 * Cada entrada incluye:
 *  - cups: código oficial del procedimiento (puede repetirse cuando hay variantes).
 *  - nombre: nombre canónico usado como `ProcedureType` y mostrado en la UI.
 *  - consentimiento: define qué consentimiento médico debe cargar la plataforma:
 *      - `{ kind: 'no_aplica' }` — no requiere consentimiento firmado.
 *      - `{ kind: 'estandar', archivo }` — un PDF único para todos los médicos.
 *      - `{ kind: 'especifico_medico', archivoPorMedico }` — el archivo depende
 *        del médico (clave = subcadena en minúsculas que debe contener el nombre).
 *
 * Los PDFs viven en `apps/web/public/consents/` y se sirven con la ruta
 * `/consents/<archivo>` desde el frontend.
 */
export type ConsentRequirement =
  | { kind: 'no_aplica' }
  | { kind: 'estandar'; archivo: string }
  | { kind: 'especifico_medico'; archivoPorMedico: Record<string, string> };

export interface ProcedureCatalogEntry {
  readonly cups: string;
  readonly nombre: string;
  readonly consentimiento: ConsentRequirement;
  /**
   * Indica si este procedimiento requiere `Registro de anestesia`. Cuando es
   * `false` (consultas, manometrías, pHmetría, elastografía, test de aliento),
   * la UI clínica oculta el botón de firma y la tarjeta del documento.
   * Default `true` cuando se omite — la mayoría de los procedimientos
   * endoscópicos sí requieren registro.
   */
  readonly requiereAnestesia?: boolean;
  /**
   * Indica si este procedimiento requiere `Hoja de gasto`. Aplica el mismo
   * patrón que `requiereAnestesia`. Default `true` cuando se omite.
   */
  readonly requiereGasto?: boolean;
}

export const PROCEDURES_CATALOG: readonly ProcedureCatalogEntry[] = [
  // Consultas — no requieren consentimiento, ni anestesia, ni hoja de gasto
  {
    cups: '890246',
    nombre: 'Consulta de primera vez por especialista en gastroenterología',
    consentimiento: { kind: 'no_aplica' },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '890346',
    nombre: 'Consulta de control o de seguimiento por especialista en gastroenterología',
    consentimiento: { kind: 'no_aplica' },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '890253',
    nombre: 'Consulta de primera vez por especialista en hepatología',
    consentimiento: { kind: 'no_aplica' },
    requiereAnestesia: false,
    requiereGasto: false,
  },

  // Procedimientos con consentimiento estándar
  {
    cups: '970200',
    nombre: 'Sustitución de tubo (sonda) de gastrostomía SOD',
    consentimiento: {
      kind: 'estandar',
      archivo: 'SUSTITUCIÓN DE TUBO (SONDA) DE GASTROSTOMÍA.pdf',
    },
  },
  {
    cups: '451302',
    nombre: 'Enteroscopia o endoscopia de intestino delgado después de duodeno (videocápsula)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ENTEROSCOPIA O ENDOSCOPIA DE INTESTINO DELGADO DESPUES DE DUODENO  (VIDEOCAPSULA).pdf',
    },
  },
  {
    cups: '881317',
    nombre: 'Ecografía endoscópica biliopancreática con o sin punción',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ECOGRAFIA ENDOSCOPICA BILIOPANCREATICA CON O SIN PUNCIÓN.pdf',
    },
  },
  {
    cups: '881319',
    nombre: 'Ecografía endoscópica de recto con o sin punción',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ECOGRAFIA ENDOSCOPICA DE RECTO CON O SIN PUNCIÓN.pdf',
    },
  },
  {
    cups: '881312',
    nombre: 'Ecografía endoscópica de esófago con o sin punción',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ECOGRAFÍA ENDOSCÓPICA DE ESÓFAGO CON O SIN PUNCIÓN .pdf',
    },
  },
  {
    cups: '881314',
    nombre: 'Ecografía endoscópica de estómago con o sin punción',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ECOGRAFÍA ENDOSCÓPICA DE ESTOMAGO CON O SIN PUNCIÓN .pdf',
    },
  },
  {
    cups: '451302',
    nombre: 'Enteroscopia o endoscopia de intestino delgado después de duodeno (argón plasma)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ENTEROSCOPIA O ENDOSCOPIA DE INTESTINO DELGADO DESPUES DE DUODENO  (ARGON PLASMA).pdf',
    },
  },
  {
    cups: '429209',
    nombre: 'Dilatación esofágica con dispositivo (con balón)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'DILATACION ESOFAGICA CON DISPOSITIVO (CON BALON).pdf',
    },
  },
  {
    cups: '489400',
    nombre: 'Dilatación instrumental endoscópica de recto SOD',
    consentimiento: {
      kind: 'estandar',
      archivo: 'DILATACIÓN INSTRUMENTAL ENDOSCÓPICA DE RECTO .pdf',
    },
  },
  {
    cups: '423305',
    nombre: 'Ligadura de várices esofágicas vía endoscópica',
    consentimiento: {
      kind: 'estandar',
      archivo: 'LIGADURA DE VARICES ESOFAGICAS VIA ENDOSCOPICA.pdf',
    },
  },
  {
    cups: '893201',
    nombre: 'Manometría esofágica',
    consentimiento: { kind: 'estandar', archivo: 'MANOMETRIA ESOFAGICA.pdf' },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '482701',
    nombre: 'Manometría anorrectal',
    consentimiento: { kind: 'estandar', archivo: 'MANOMETRIA ANORECTAL.pdf' },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '434102',
    nombre: 'Control endoscópico de hemorragia gástrica mediante escleroterapia (argón plasma)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'CONTROL ENDOSCOPICO DE HEMORRAGIA GASTRICA MEDIANTE ESCLEROTERAPIA (ARGON PLASMA).pdf',
    },
  },
  {
    cups: '454203',
    nombre: 'Control de hemorragia de colon y recto vía endoscópica (argón plasma)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'CONTROL DE HEMORRAGIA DE COLON Y RECTO VIA ENDOSCOPICA (ARGON PLASMA).pdf',
    },
  },
  {
    cups: '893905',
    nombre: 'Monitoreo de pH esofágico en 24 horas (pHmetría)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'MONITOREO DE PH ESOFAGICO EN 24 HORAS [PHMETRIA].pdf',
    },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '452305',
    nombre: 'Colonoscopia total con o sin biopsia',
    consentimiento: { kind: 'estandar', archivo: 'COLONOSCOPIA TOTAL CON O SIN BIOSIA.pdf' },
  },
  {
    cups: '441302',
    nombre: 'Esofagogastroduodenoscopia diagnóstica con o sin biopsia',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ESOFAGOGASTRODUEDENOSCOPIA DIAGNOSTICA CON O SIN BIOPSIA .pdf',
    },
  },
  {
    cups: '452401',
    nombre: 'Sigmoidoscopia flexible o rígida',
    consentimiento: { kind: 'estandar', archivo: 'SIGMOIDOSCOPIA FLEXIBLE O RIGIDA.pdf' },
  },
  {
    cups: '434001',
    nombre: 'Escisión de pólipos gástricos vía endoscópica',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ESCISION DE POLIPOS GASTRICOS VIA ENDOSCOPICA.pdf',
    },
  },
  {
    cups: '423301',
    nombre: 'Polipectomía endoscópica de esófago',
    consentimiento: { kind: 'estandar', archivo: 'POLIPECTOMIA ENDOSCOPICA DE ESOFAGO.pdf' },
  },
  {
    cups: '431002',
    nombre: 'Gastrostomía vía percutánea [endoscópica]',
    consentimiento: { kind: 'estandar', archivo: 'GASTROSTOMÍA VIA PERCUTANEA [ENDOSCOPICA].pdf' },
  },
  {
    cups: '429405',
    nombre: 'Extracción endoscópica de cuerpo extraño en esófago',
    consentimiento: { kind: 'estandar', archivo: 'EXTRACCION ENDOSCOPICA DE CUERPO EXTRAÑO.pdf' },
  },
  {
    cups: '434500',
    nombre: 'Mucosectomía endoscópica gástrica SOD',
    consentimiento: { kind: 'estandar', archivo: 'MUCOSECTOMIA ENDOSCOPICA GASTRICA SOD.pdf' },
  },
  {
    cups: '454205',
    nombre: 'Mucosectomía de colon o recto por vía endoscópica SOD',
    consentimiento: {
      kind: 'estandar',
      archivo: 'MUCOSECTOMIA DE COLON O RECTO POR VIA ENDOSCOPICA.pdf',
    },
  },
  {
    // Comparte consentimiento con CPRE (511000)
    cups: '518801',
    nombre: 'Extracción endoscópica de cálculos de las vías biliares con esfinterotomía',
    consentimiento: {
      kind: 'estandar',
      archivo: 'COLANGIO-PANCREATOGRAFÍA RETRÓGRADA ENDOSCÓPICA (CPRE) .pdf',
    },
  },
  {
    cups: '512302',
    nombre: 'Litotripsia intraductal por coledocoscopia (SpyGlass)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'LITOTRIPSIA INTRADUCTAL POR COLEDOCOSCOPIA (SPYGLASS).pdf',
    },
  },
  {
    cups: '524101',
    nombre: 'Drenaje transgástrico endoscópico de quiste o seudoquiste pancreático (incluye stent metálico autoexpandible)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'DRENAJE TRANSGASTRICO ENDOSCOPICO DE QUISTE O SEUDOQUISTE PANCREATICO INCLUYE STENT METALICO AUTOEXPANDIBLE.pdf',
    },
  },
  {
    // Reutiliza el consentimiento de drenaje transgástrico
    cups: '524101',
    nombre: 'Drenaje de pseudoquiste de páncreas con prótesis',
    consentimiento: {
      kind: 'estandar',
      archivo: 'DRENAJE TRANSGASTRICO ENDOSCOPICO DE QUISTE O SEUDOQUISTE PANCREATICO INCLUYE STENT METALICO AUTOEXPANDIBLE.pdf',
    },
  },
  {
    // Reutiliza el consentimiento de drenaje transgástrico
    cups: '524101',
    nombre: 'Drenaje y/o necrosectomía de pseudoquiste de páncreas sin prótesis por sesión',
    consentimiento: {
      kind: 'estandar',
      archivo: 'DRENAJE TRANSGASTRICO ENDOSCOPICO DE QUISTE O SEUDOQUISTE PANCREATICO INCLUYE STENT METALICO AUTOEXPANDIBLE.pdf',
    },
  },
  {
    cups: '513600',
    nombre: 'Coledocoduodenostomía o colecistogastrostomía endoscópica con prótesis',
    consentimiento: {
      kind: 'estandar',
      archivo: 'COLEDOCODUDENOSTOMIA O COLECISTO GASTROSTOMIA ENDOSCÓPICA CON PROTESIS.pdf',
    },
  },
  {
    cups: '524101',
    nombre: 'Hepatogastrostomía endoscópica con prótesis Gibor y Cristomo de 6 Fr',
    consentimiento: {
      kind: 'estandar',
      archivo: 'HEPATOGASTROTOMIA ENDOSCOPICA CON PROTESIS GIBOR Y CRISTOMO DE 6FR.pdf',
    },
  },
  {
    cups: '523200',
    nombre: 'Ablación por radiofrecuencia de tumor de páncreas',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ABLACIÓN POR RADIOFRECUENCIA DE TUMOR DE PÁNCREAS.pdf',
    },
  },
  {
    cups: '434104',
    nombre: 'Obturación de várices gástricas por endosonografía con coils y sellante',
    consentimiento: {
      kind: 'estandar',
      archivo: 'OBTURACION DE VARICES GASTRICAS POR ENDOSONOGRAFIA  CON COILS Y SELLANTE.pdf',
    },
  },
  {
    cups: '438300',
    nombre: 'Gastroenteroanastomosis endoscópica con prótesis guiada por endosonografía (EUS-GEA)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'GASTROENTEROANASTOMOSIS ENDOSCÓPICA CON PRÓTESIS GUIADA POR ENDOSONOGRAFÍA (EUS-GEA).pdf',
    },
  },
  {
    cups: '427403',
    nombre: 'POEM, G-POEM y Z-POEM (miotomía endoscópica per oral), resección de tumores submucosos y disección endoscópica de la submucosa',
    consentimiento: {
      kind: 'estandar',
      archivo: 'POEM, G POEM Y Z POEM (MIOTOMIA ENDOSCOPICA PER ORAL), RESECCION DE TUMORES SUBMUCOSOS Y DISECCIÓN ENDOSCÓPICA DE LA SUBMUCOSA.pdf',
    },
  },
  {
    cups: '467200',
    nombre: 'Colocación de clips de OVESCO para cierre de fístula o perforación digestiva',
    consentimiento: {
      kind: 'estandar',
      archivo: 'COLOCACIÓN DE CLIPS DE OVESCO PARA CIERRE DE FÍSTULA O PERFORACIÓN DIGESTIVA.pdf',
    },
  },
  {
    cups: '511000',
    nombre: 'Colangio-pancreatografía retrógrada endoscópica (CPRE)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'COLANGIO-PANCREATOGRAFÍA RETRÓGRADA ENDOSCÓPICA (CPRE) .pdf',
    },
  },
  {
    cups: '492102',
    nombre: 'Anoscopia de alta resolución',
    consentimiento: { kind: 'estandar', archivo: 'ANOSCOPIA DE ALTA RESOLUCION .pdf' },
  },
  {
    cups: '886101',
    nombre: 'Elastografía hepática',
    consentimiento: { kind: 'estandar', archivo: 'ELASTOGRAFIA HEPATICA.pdf' },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '903043',
    nombre: 'Test de aliento (prueba de aliento 13C urea para Helicobacter pylori)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'PRUEBA DE ALIENTO 13C UREA PARA HELICOBACTER PYLORI.pdf',
    },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '893824',
    nombre: 'Prueba de aliento para determinación de hidrógeno y metano (SIBO)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'PRUEBA DE ALIENTO HIDROGENO Y METANO SIBO.pdf',
    },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '9030435',
    nombre: 'Test de aliento con fructosa (sobrecrecimiento bacteriano)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'TEST DE ALIENTO CON FRUCTOSA.pdf',
    },
    requiereAnestesia: false,
    requiereGasto: false,
  },
  {
    cups: '423308',
    nombre: 'Ablación esofágica por RFA Barrxs HaloFlex',
    consentimiento: {
      kind: 'estandar',
      archivo: 'ABLACIÓN ESOFÁGICA POR RFA BARRXS HALOFLEX.pdf',
    },
  },
  {
    cups: '293102',
    nombre: 'Resección de tumores submucosos y disección endoscópica de la submucosa',
    consentimiento: {
      kind: 'estandar',
      archivo: 'RESECCION DE TUMORES SUBMUCOSOS Y DISECCION ENDOSCOPICA DE LA SUBMUCOSA .pdf',
    },
  },
  {
    cups: '449301A',
    nombre: 'Inserción de dispositivo intragástrico restrictivo por endoscopia (balón Allurion)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'INSERCIÓN DE DISPOSITIVO INTRAGÁSTRICO RESTRICTIVO POR ENDOSCOPIA (BALON ALLURION).pdf',
    },
  },
  {
    // Específico Catherine Fiorillo
    cups: '449301C',
    nombre: 'Inserción de dispositivo intragástrico restrictivo por endoscopia (Dra. Catherine Fiorillo)',
    consentimiento: {
      kind: 'especifico_medico',
      archivoPorMedico: {
        fiorillo:
          'INSERCIÓN DE DISPOSITIVO INTRAGÁSTRICO RESTRICTIVO POR ENDOSCOPIA - CATHERINE FIORILLO.pdf',
      },
    },
  },
  {
    cups: '449301E',
    nombre: 'Extracción de balón (inserción de dispositivo intragástrico restrictivo por endoscopia)',
    consentimiento: {
      kind: 'estandar',
      archivo:
        'EXTRACCION DE BALON (INSERCIÓN DE DISPOSITIVO INTRAGÁSTRICO RESTRICTIVO POR ENDOSCOPIA).pdf',
    },
  },
  {
    // Específico Jesús Pérez
    cups: '449301J',
    nombre: 'Inserción de dispositivo intragástrico restrictivo por endoscopia (Dr. Jesús Pérez)',
    consentimiento: {
      kind: 'especifico_medico',
      archivoPorMedico: {
        perez:
          'INSERCIÓN DE DISPOSITIVO INTRAGÁSTRICO RESTRICTIVO POR ENDOSCOPIA -DR JESUS PEREZ.pdf',
        'pérez':
          'INSERCIÓN DE DISPOSITIVO INTRAGÁSTRICO RESTRICTIVO POR ENDOSCOPIA -DR JESUS PEREZ.pdf',
      },
    },
  },
  {
    // Específico Catherine Fiorillo
    cups: '446503C',
    nombre: 'Gastroplastia vía endoscópica (Dra. Catherine Fiorillo)',
    consentimiento: {
      kind: 'especifico_medico',
      archivoPorMedico: {
        fiorillo: 'GASTROPLASTIA VÍA ENDOSCÓPICA-CATHERINE FIORILLO.pdf',
      },
    },
  },
  {
    // Específico Jesús Pérez
    cups: '446503J',
    nombre: 'Gastroplastia vía endoscópica (Dr. Jesús Pérez)',
    consentimiento: {
      kind: 'especifico_medico',
      archivoPorMedico: {
        perez: 'GASTROPLASTIA VÍA ENDOSCOPICA - DR JESUS PEREZ.pdf',
        'pérez': 'GASTROPLASTIA VÍA ENDOSCOPICA - DR JESUS PEREZ.pdf',
      },
    },
  },
  {
    cups: '494001',
    nombre: 'Ligadura de hemorroides',
    consentimiento: { kind: 'estandar', archivo: 'LIGADURA DE HEMORROIDES.pdf' },
  },
  {
    cups: '454208',
    nombre: 'Resección de lesión de intestino grueso vía endoscópica (4-10)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'RESECCION DE LESION DE INTESTINO GRUESO VIA ENDOSCOPICA (4-10).pdf',
    },
  },
  {
    cups: '454207',
    nombre: 'Resección de lesión de intestino grueso vía endoscópica (1-3)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'RESECCION DE LESION DE INTESTINO GRUESO VIA ENDOSCOPICA (1-3).pdf',
    },
  },
  {
    cups: '454209',
    nombre: 'Resección de lesión de intestino grueso vía endoscópica (11 o más)',
    consentimiento: {
      kind: 'estandar',
      archivo: 'RESECCION DE LESION DE INTESTINO GRUESO VIA ENDOSCOPICA (11 O MAS).pdf',
    },
  },
  {
    cups: '446503T',
    nombre: 'Gastroplastia vía endoscópica (TORE)',
    consentimiento: { kind: 'estandar', archivo: 'GASTROPLASTIA VÍA ENDOSCÓPICA (TORE).pdf' },
  },
] as const;

/** Devuelve la entrada del catálogo cuyo nombre coincide con `tipo`, o `undefined`. */
export function findCatalogEntry(tipo: string): ProcedureCatalogEntry | undefined {
  return PROCEDURES_CATALOG.find((p) => p.nombre === tipo);
}

/**
 * ¿Este procedimiento requiere `Registro de anestesia`? Default `true` —
 * solo es `false` para los procedimientos marcados explícitamente en el
 * catálogo (consultas, manometrías, pHmetría, elastografía, test de aliento).
 * Si el `tipo` no está en el catálogo (legado), por seguridad se asume `true`
 * para no esconder un documento que debería existir.
 */
export function requiresAnesthesia(tipo: string): boolean {
  const entry = findCatalogEntry(tipo);
  if (entry === undefined) return true;
  return entry.requiereAnestesia !== false;
}

/** Mismo patrón que `requiresAnesthesia` para la hoja de gasto. */
export function requiresExpense(tipo: string): boolean {
  const entry = findCatalogEntry(tipo);
  if (entry === undefined) return true;
  return entry.requiereGasto !== false;
}

/**
 * Devuelve las claves de documentos clínicos que el procedimiento sí necesita.
 * Útil para iterar en la UI sin condicionales por documento.
 */
export type RequiredDocKey = 'consentimiento' | 'anestesia' | 'gasto';
export function requiredDocs(tipo: string): RequiredDocKey[] {
  const entry = findCatalogEntry(tipo);
  const requiereConsent = entry?.consentimiento.kind !== 'no_aplica';
  const docs: RequiredDocKey[] = [];
  if (requiereConsent) docs.push('consentimiento');
  if (requiresAnesthesia(tipo)) docs.push('anestesia');
  if (requiresExpense(tipo)) docs.push('gasto');
  return docs;
}

/**
 * Igual que `requiredDocs` pero para un episodio con MÚLTIPLES procedimientos.
 * Un documento es requerido si CUALQUIERA de los procedimientos lo requiere
 * (la hoja de gasto / record de anestesia se llenan una sola vez para todo
 * el episodio aunque haya varios CUPS; el consentimiento se carga por cada
 * uno desde la UI).
 */
export function requiredDocsForEpisode(tipos: ReadonlyArray<string>): RequiredDocKey[] {
  const docs: Set<RequiredDocKey> = new Set();
  for (const t of tipos) {
    for (const d of requiredDocs(t)) docs.add(d);
  }
  // Preservamos el orden lógico (consent → anestesia → gasto)
  const ORDER: RequiredDocKey[] = ['consentimiento', 'anestesia', 'gasto'];
  return ORDER.filter((d) => docs.has(d));
}

/** Devuelve los nombres únicos del catálogo, ordenados alfabéticamente. */
export function listProcedureNames(): string[] {
  return PROCEDURES_CATALOG.map((p) => p.nombre).sort((a, b) => a.localeCompare(b, 'es'));
}

/**
 * Resuelve la URL pública del PDF de consentimiento que debe cargarse para
 * un (tipo, médico) determinado. Devuelve `null` si el procedimiento no
 * requiere consentimiento, o si el médico no coincide con un consentimiento
 * específico requerido.
 *
 * Convenciones:
 *  - Los PDFs se sirven desde `/consents/<archivo>` (carpeta `public/consents/`).
 *  - Para `especifico_medico`, el nombre del médico se compara en minúsculas
 *    contra cada clave del mapa (subcadena). Ej. `'dra. catherine fiorillo'`
 *    matchea la clave `'fiorillo'`.
 */
export function resolveConsentUrl(
  tipo: string,
  medicoNombre: string | undefined,
): string | null {
  const entry = findCatalogEntry(tipo);
  if (entry === undefined) return null;
  const file = resolveConsentFile(entry, medicoNombre);
  if (file === null) return null;
  return `/consents/${encodeURIComponent(file)}`;
}

/** Devuelve el nombre del archivo (no la URL) — útil para tests y backend. */
export function resolveConsentFile(
  entry: ProcedureCatalogEntry,
  medicoNombre: string | undefined,
): string | null {
  const c = entry.consentimiento;
  if (c.kind === 'no_aplica') return null;
  if (c.kind === 'estandar') return c.archivo;
  if (medicoNombre === undefined || medicoNombre.trim() === '') return null;
  const lower = medicoNombre.toLowerCase();
  for (const [key, archivo] of Object.entries(c.archivoPorMedico)) {
    if (lower.includes(key)) return archivo;
  }
  return null;
}
